import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { rupiah, num } from '../lib/format';
import { Plus, Pencil, Trash, X, Sparkle, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { AiProductDialog } from '../components/ai/AiProductDialog';
import { ProductPhoto } from '../components/ProductPhoto';

const SATUAN_JUAL = ['botol', 'pack', 'pcs', 'bungkus', 'box'];

const statusColor = {
  Aman: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Menipis: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Habis: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

const emptyForm = {
  nama: '', satuan_jual: 'pack', hasil_per_produksi: 1,
  biaya_lain_per_unit: 0, target_margin: 30, potongan_default: 0,
  harga_jual_manual: '', stok: 0, stok_min: 0, resep: [],
};

export default function Produk() {
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [aiFor, setAiFor] = useState(null);

  const load = async () => {
    const [p, m] = await Promise.all([api.get('/products'), api.get('/materials')]);
    setProducts(p.data);
    setMaterials(m.data);
  };
  useEffect(() => { load(); }, []);

  // Live preview
  useEffect(() => {
    if (!showForm) return;
    const payload = {
      nama: form.nama || 'preview',
      satuan_jual: form.satuan_jual,
      resep: form.resep.filter(r => r.material_id).map(r => ({ material_id: r.material_id, qty: parseFloat(r.qty) || 0 })),
      hasil_per_produksi: parseFloat(form.hasil_per_produksi) || 1,
      biaya_lain_per_unit: parseFloat(form.biaya_lain_per_unit) || 0,
      target_margin: parseFloat(form.target_margin) || 0,
      potongan_default: parseFloat(form.potongan_default) || 0,
      harga_jual_manual: form.harga_jual_manual ? parseFloat(form.harga_jual_manual) : null,
    };
    const t = setTimeout(() => {
      api.post('/products/preview', payload).then((r) => setPreview(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [form, showForm]);

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      nama: p.nama, satuan_jual: p.satuan_jual,
      hasil_per_produksi: p.hasil_per_produksi, biaya_lain_per_unit: p.biaya_lain_per_unit,
      target_margin: p.target_margin, potongan_default: p.potongan_default,
      harga_jual_manual: p.harga_jual_manual || '', stok: p.stok, stok_min: p.stok_min,
      resep: p.resep || [],
    });
    setShowForm(true);
  };

  const resetForm = () => { setForm(emptyForm); setEditId(null); setShowForm(false); setPreview(null); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      hasil_per_produksi: parseFloat(form.hasil_per_produksi) || 1,
      biaya_lain_per_unit: parseFloat(form.biaya_lain_per_unit) || 0,
      target_margin: parseFloat(form.target_margin) || 0,
      potongan_default: parseFloat(form.potongan_default) || 0,
      harga_jual_manual: form.harga_jual_manual ? parseFloat(form.harga_jual_manual) : null,
      stok: parseFloat(form.stok) || 0,
      stok_min: parseFloat(form.stok_min) || 0,
      resep: form.resep.filter(r => r.material_id).map(r => ({ material_id: r.material_id, qty: parseFloat(r.qty) || 0 })),
    };
    try {
      if (editId) await api.patch(`/products/${editId}`, payload);
      else await api.post('/products', payload);
      toast.success(editId ? 'Produk diperbarui' : 'Produk ditambahkan');
      resetForm();
      load();
    } catch (e) {
      toast.error('Gagal menyimpan produk');
    }
  };

  const del = async (id) => {
    if (confirmDel !== id) { setConfirmDel(id); setTimeout(() => setConfirmDel(null), 3000); return; }
    await api.delete(`/products/${id}`);
    toast.success('Produk dihapus');
    setConfirmDel(null);
    load();
  };

  const addResepRow = () => setForm({ ...form, resep: [...form.resep, { material_id: '', qty: '' }] });
  const updateResep = (i, patch) => {
    const r = [...form.resep];
    r[i] = { ...r[i], ...patch };
    setForm({ ...form, resep: r });
  };
  const removeResep = (i) => setForm({ ...form, resep: form.resep.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-muted-foreground text-sm max-w-lg">HPP, harga jual saran dan laba bersih dihitung otomatis saat Anda mengisi formulir.</p>
        <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="btn-add-product"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90">
          <Plus size={18} weight="bold" /> Tambah Produk
        </button>
      </div>

      {showForm && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <form onSubmit={submit} data-testid="form-product" className="lg:col-span-2 bg-card border border-border/60 rounded-xl p-5 lg:p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">{editId ? 'Edit Produk' : 'Tambah Produk'}</h3>
              <button type="button" onClick={resetForm}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nama Produk" value={form.nama} onChange={(v) => setForm({ ...form, nama: v })} required testid="prod-nama" />
              <SelectField label="Satuan Jual" value={form.satuan_jual} options={SATUAN_JUAL} onChange={(v) => setForm({ ...form, satuan_jual: v })} testid="prod-satuan" />
              <Field label="Hasil per Produksi (unit)" type="number" value={form.hasil_per_produksi} onChange={(v) => setForm({ ...form, hasil_per_produksi: v })} testid="prod-hasil" />
              <Field label="Biaya Lain per Unit (kemasan, label, tenaga, listrik)" type="number" value={form.biaya_lain_per_unit} onChange={(v) => setForm({ ...form, biaya_lain_per_unit: v })} testid="prod-biayalain" />
              <Field label="Target Margin (%)" type="number" value={form.target_margin} onChange={(v) => setForm({ ...form, target_margin: v })} testid="prod-margin" />
              <Field label="Potongan Penjualan (%)" type="number" value={form.potongan_default} onChange={(v) => setForm({ ...form, potongan_default: v })} testid="prod-potongan" />
              <Field label="Harga Jual Manual (opsional)" type="number" value={form.harga_jual_manual} onChange={(v) => setForm({ ...form, harga_jual_manual: v })} testid="prod-hargamanual" />
              <Field label="Stok Produk Jadi" type="number" value={form.stok} onChange={(v) => setForm({ ...form, stok: v })} testid="prod-stok" />
              <Field label="Batas Stok Minimum" type="number" value={form.stok_min} onChange={(v) => setForm({ ...form, stok_min: v })} testid="prod-stokmin" />
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">Resep (bahan untuk 1x produksi)</h4>
                <button type="button" onClick={addResepRow} data-testid="btn-add-resep" className="text-sm px-3 py-1.5 rounded-lg border border-border hover:bg-secondary">+ Tambah Bahan</button>
              </div>
              <div className="space-y-2">
                {form.resep.length === 0 && <div className="text-sm text-muted-foreground py-3">Belum ada bahan. Klik "Tambah Bahan".</div>}
                {form.resep.map((r, i) => {
                  const mat = materials.find((m) => m.id === r.material_id);
                  return (
                    <div key={i} className="flex gap-2 items-center">
                      <select value={r.material_id} onChange={(e) => updateResep(i, { material_id: e.target.value })}
                        data-testid={`resep-mat-${i}`}
                        className="flex-1 px-3 py-2 bg-background border border-border rounded-lg">
                        <option value="">Pilih bahan…</option>
                        {materials.map((m) => <option key={m.id} value={m.id}>{m.nama} ({m.satuan})</option>)}
                      </select>
                      <input type="number" step="any" placeholder="Jumlah" value={r.qty} onChange={(e) => updateResep(i, { qty: e.target.value })}
                        data-testid={`resep-qty-${i}`}
                        className="w-32 px-3 py-2 bg-background border border-border rounded-lg" />
                      <span className="text-sm text-muted-foreground w-14">{mat?.satuan || ''}</span>
                      <button type="button" onClick={() => removeResep(i)} className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg"><Trash size={18} /></button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="submit" data-testid="btn-submit-product" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium">
                {editId ? 'Simpan Perubahan' : 'Simpan Produk'}
              </button>
              <button type="button" onClick={resetForm} className="px-5 py-2.5 border border-border rounded-lg font-medium hover:bg-secondary">Batal</button>
            </div>
          </form>

          {/* Preview */}
          <aside className="bg-card border border-primary/30 rounded-xl p-5 lg:p-6 h-fit lg:sticky lg:top-24" data-testid="product-preview">
            <h4 className="font-display font-semibold text-lg mb-4 flex items-center justify-between">
              Rincian Perhitungan
              <span className="text-xs font-normal text-muted-foreground">live</span>
            </h4>
            <div className="space-y-3 text-sm">
              <PreviewRow label="Total Biaya Bahan" value={rupiah(preview?.total_biaya_bahan)} />
              <PreviewRow label="HPP Bahan / Unit" value={rupiah(preview?.hpp_bahan_per_unit)} />
              <PreviewRow label="Biaya Lain / Unit" value={rupiah(parseFloat(form.biaya_lain_per_unit) || 0)} />
              <div className="h-px bg-border my-2" />
              <PreviewRow label="HPP per Unit" value={rupiah(preview?.hpp_per_unit)} bold />
              <PreviewRow label={`Harga Saran (margin ${form.target_margin}% − potongan ${form.potongan_default}%)`} value={rupiah(preview?.harga_saran)} />
              <PreviewRow label="Harga Jual Efektif" value={rupiah(preview?.harga_jual_efektif)} bold />
              <PreviewRow label="Potongan" value={`− ${rupiah(preview?.potongan_nilai)}`} />
              <div className="h-px bg-border my-2" />
              <PreviewRow
                label="Laba Bersih / Unit"
                value={<span className="text-emerald-600 dark:text-emerald-400 font-semibold">{rupiah(preview?.laba_per_unit)}</span>}
                bold
              />
              <PreviewRow label="Margin Aktual" value={`${num(preview?.margin_aktual, 1)}%`} />
            </div>
          </aside>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="list-products">
        {products.length === 0 && (
          <div className="col-span-full bg-card border border-border/60 rounded-xl p-8 text-center text-muted-foreground">
            Belum ada produk. Klik "Tambah Produk" atau muat contoh data.
          </div>
        )}
        {products.map((p) => (
          <div key={p.id} className="bg-card border border-border/60 rounded-xl p-5 space-y-3 overflow-hidden">
            <ProductPhoto product={p} onChanged={load} />
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg font-semibold">{p.nama}</div>
                <div className="text-xs text-muted-foreground">per {p.satuan_jual} {p.is_sample && '· contoh'}</div>
                {p.metrics?.bahan_hilang > 0 && <div className="text-xs text-rose-600 mt-1" data-testid={`warn-missing-${p.id}`}>{p.metrics.bahan_hilang} bahan di resep sudah dihapus. Edit resep dulu.</div>}
              </div>
              <span className={`px-2 py-1 rounded-md text-xs font-medium ${statusColor[p.status]}`}>{p.status}</span>
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 text-sm">
              <span className="text-muted-foreground">HPP / unit</span>
              <span className="text-right tabular-nums">{rupiah(p.metrics?.hpp_per_unit)}</span>
              <span className="text-muted-foreground">Harga Jual</span>
              <span className="text-right tabular-nums font-semibold">{rupiah(p.metrics?.harga_jual_efektif)}</span>
              <span className="text-muted-foreground">Laba / unit</span>
              <span className="text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">{rupiah(p.metrics?.laba_per_unit)}</span>
              <span className="text-muted-foreground">Margin</span>
              <span className="text-right tabular-nums">{num(p.metrics?.margin_aktual, 1)}%</span>
              <span className="text-muted-foreground">Stok</span>
              <span className="text-right tabular-nums">{num(p.stok, 2)} {p.satuan_jual} <span className="text-xs text-muted-foreground">(min {num(p.stok_min)})</span></span>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => openEdit(p)} data-testid={`btn-edit-prod-${p.id}`} className="flex-1 px-3 py-2 border border-border rounded-lg text-sm hover:bg-secondary flex items-center justify-center gap-1.5"><Pencil size={16} /> Edit</button>
              <button onClick={() => del(p.id)} data-testid={`btn-del-prod-${p.id}`} className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 ${confirmDel === p.id ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-600' : 'border border-border hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-500'}`}>
                <Trash size={16} /> {confirmDel === p.id ? 'Konfirmasi?' : 'Hapus'}
              </button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setAiFor({ product: p, mode: 'pricing' })} data-testid={`btn-ai-pricing-${p.id}`}
                className="flex-1 px-3 py-2 rounded-lg text-sm bg-primary/10 text-primary hover:bg-primary/15 flex items-center justify-center gap-1.5 transition-colors">
                <Tag size={16} weight="duotone" /> Saran Harga AI
              </button>
              <button onClick={() => setAiFor({ product: p, mode: 'description' })} data-testid={`btn-ai-desc-${p.id}`}
                className="flex-1 px-3 py-2 rounded-lg text-sm bg-primary/10 text-primary hover:bg-primary/15 flex items-center justify-center gap-1.5 transition-colors">
                <Sparkle size={16} weight="duotone" /> Tulis Deskripsi
              </button>
            </div>
          </div>
        ))}
      </div>
      {aiFor && <AiProductDialog key={`${aiFor.product.id}-${aiFor.mode}`} product={aiFor.product} mode={aiFor.mode} onClose={() => setAiFor(null)} />}
    </div>
  );
}

function PreviewRow({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${bold ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} step="any"
        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40" />
    </label>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
